/**
 * MetricsComparisonSection — Peer comparison cards for Revenue CAGR, EBITDA, ROIC, WACC.
 *
 * Mirrors the Financial-Insight-Dashboard "Financial Metrics Comparison" tab,
 * excluding the Sensitivity Analysis sliders (those live on the Sensitivity tab).
 *
 * Each metric is shown as a card with:
 *   - Company vs peer KPI values
 *   - Performance indicator (good / poor / neutral)
 *   - Recharts bar chart comparing company to peers
 */

import { useMemo } from 'react';
import { AlertCircle, TrendingUp, TrendingDown, Minus, BarChart3, Percent, Target, Scale } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import LoadingCard from '../../ui/LoadingCard';
import { useFinancialMetricsComparison } from '../hooks/useCompanyFinancials';
import {
  FOCAL_COLOR,
  PEER_COLORS,
  PEER_BAR_UNIFORM,
  BAR_MAX_SIZE,
  gridProps,
  yAxisProps,
  zeroLineProps,
  CHART_INK,
  CHART_TYPE,
} from '../charts/chartTheme';
import { focalTickFactory } from '../charts/ChartCard';
import ChartTooltip from '../charts/ChartTooltip';
import type { FinancialMetricsComparisonResponse, FinancialMetricDetail } from '../../../types/financial';

// ── Helpers ──────────────────────────────────────────────────────────────────

// Every chart here is single-value (latest only — the 10-Yr average lives in the
// KPI boxes above), so every bar paints the uniform peer tone (PEER_BAR_UNIFORM)
// and the focal company is marked by its ★ axis tick instead of a distinct fill —
// exactly like the CompetitorEVChart / CompetitorCAGRChart family.
const COLORS = {
  primary: FOCAL_COLOR, // deep brand purple
};

function formatPct(v: number | string | null | undefined, decimals = 2): string {
  if (v == null || v === '') return 'N/A';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return 'N/A';
  return `${n.toFixed(decimals)}%`;
}

function formatVal(v: number | string | null | undefined, decimals = 2): string {
  if (v == null || v === '') return 'N/A';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return 'N/A';
  return n.toFixed(decimals);
}

type Performance = 'good' | 'poor' | 'neutral';

function getPerformance(
  companyVal: number | string | null | undefined,
  peerVal: number | string | null | undefined,
  higherIsBetter = true,
): Performance {
  const a = typeof companyVal === 'number' ? companyVal : parseFloat(String(companyVal ?? ''));
  const b = typeof peerVal === 'number' ? peerVal : parseFloat(String(peerVal ?? ''));
  if (isNaN(a) || isNaN(b)) return 'neutral';
  const diff = a - b;
  if (Math.abs(diff) < 0.01) return 'neutral';
  return higherIsBetter ? (diff > 0 ? 'good' : 'poor') : (diff < 0 ? 'good' : 'poor');
}

// ── Shared chart wrapper ─────────────────────────────────────────────────────

interface ChartEntry {
  name: string;
  isCompany: boolean;
  [key: string]: any;
}

function ComparisonBarChart({
  data,
  dataKeys,
  yLabel,
  tooltipSuffix = '',
}: {
  data: ChartEntry[];
  dataKeys: { key: string; name: string; fill: string }[];
  yLabel: string;
  tooltipSuffix?: string;
}) {
  if (data.length === 0) return null;

  const singleBar = dataKeys.length === 1;
  const showLegend = !singleBar;
  // Focal identity is carried by the ★ axis tick (focalTickFactory), exactly like
  // the CompetitorEVChart / CompetitorCAGRChart family — not by a distinct fill.
  const focalName = data.find((d) => d.isCompany)?.name ?? '';

  return (
    <ResponsiveContainer width="100%" height={showLegend ? 320 : 288}>
      <BarChart
        data={data}
        margin={{ top: 24, right: 20, left: 12, bottom: 68 }}
        barCategoryGap="18%"
        barGap={4}
      >
        <CartesianGrid {...gridProps} vertical={false} />
        <XAxis
          dataKey="name"
          interval={0}
          height={72}
          tickLine={false}
          axisLine={{ stroke: CHART_INK.grid }}
          tick={focalTickFactory(focalName)}
        />
        <YAxis
          {...yAxisProps}
          width={56}
          tickFormatter={(v) => `${v}${tooltipSuffix}`}
          label={{
            value: yLabel,
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: CHART_TYPE.axisTick, fill: CHART_INK.muted },
          }}
        />
        <ReferenceLine y={0} {...zeroLineProps} />
        <Tooltip
          content={<ChartTooltip format={(v) => `${v.toFixed(2)}${tooltipSuffix}`} />}
          cursor={{ fill: '#f9fafb' }}
        />
        {showLegend && (
          <Legend
            verticalAlign="bottom"
            align="center"
            iconType="square"
            wrapperStyle={{ paddingTop: 8, fontSize: CHART_TYPE.legend }}
          />
        )}
        {dataKeys.map(({ key, name, fill }) => (
          <Bar key={key} dataKey={key} name={name} fill={fill} radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
            {/* Single-value charts: uniform tone for every bar + data labels on top. */}
            {singleBar && (
              <LabelList
                dataKey={key}
                position="top"
                formatter={(v: number) => `${v.toFixed(1)}${tooltipSuffix}`}
                style={{ fontSize: CHART_TYPE.dataLabel, fill: CHART_INK.label, fontWeight: 600 }}
              />
            )}
            {singleBar && data.map((entry) => <Cell key={entry.name} fill={PEER_BAR_UNIFORM} />)}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Single Metric Card ───────────────────────────────────────────────────────

interface MetricCardEntry {
  title: string;
  icon: LucideIcon;
  companyValues: { label: string; value: string }[];
  peerValues: { label: string; value: string }[];
  performance: Performance;
  chart: JSX.Element;
}

const PERFORMANCE_META: Record<
  Performance,
  { icon: typeof TrendingUp; label: string; color: string; bg: string }
> = {
  good: { icon: TrendingUp, label: 'Outperforming', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  poor: { icon: TrendingDown, label: 'Underperforming', color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  neutral: { icon: Minus, label: 'In line', color: 'text-neutral-600', bg: 'bg-neutral-50 border-neutral-200' },
};

function KpiPill({ label, value, accent }: { label: string; value: string; accent: boolean }) {
  return (
    <div
      className={`flex flex-col rounded-lg border px-3.5 py-2.5 min-w-0 ${
        accent ? 'border-secondary-200 bg-secondary-50' : 'border-neutral-200 bg-neutral-50'
      }`}
    >
      <span className="text-[10px] font-medium text-neutral-500 leading-tight truncate" title={label}>
        {label}
      </span>
      <span
        className={`text-sm font-bold tabular-nums mt-1 leading-none ${
          accent ? 'text-secondary-900' : 'text-neutral-800'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

// A labelled group of KPI pills, prefixed with a colour dot that ties the row to
// its series in the chart below (focal purple for the company, peer tint for peers).
function KpiGroup({
  label,
  dotColor,
  labelClass,
  values,
  accent,
}: {
  label: string;
  dotColor: string;
  labelClass: string;
  values: { label: string; value: string }[];
  accent: boolean;
}) {
  const cols = values.length >= 2 ? 'grid-cols-2' : 'grid-cols-1';
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
        <p className={`text-[10px] font-semibold uppercase tracking-wider truncate ${labelClass}`}>{label}</p>
      </div>
      <div className={`grid ${cols} gap-2`}>
        {values.map((v, i) => (
          <KpiPill key={i} label={v.label} value={v.value} accent={accent} />
        ))}
      </div>
    </div>
  );
}

function MetricCard({ m, companyName }: { m: MetricCardEntry; companyName: string }) {
  const perf = PERFORMANCE_META[m.performance];
  const PerfIcon = perf.icon;
  const Icon = m.icon;

  return (
    <Card className="group flex h-full flex-col overflow-hidden rounded-xl border-neutral-200 shadow-sm transition-all duration-200 hover:border-secondary-200 hover:shadow-md">
      {/* Header — branded icon chip + metric title + performance badge */}
      <CardHeader className="border-neutral-100 bg-gradient-to-b from-neutral-50 to-white px-5 py-3.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-secondary-100 bg-secondary-50">
              <Icon className="h-4 w-4 text-secondary-700" />
            </span>
            <h3 className="truncate text-sm font-semibold tracking-tight text-neutral-900">{m.title}</h3>
          </div>
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${perf.bg} ${perf.color}`}
          >
            <PerfIcon className="h-3 w-3" />
            {perf.label}
          </span>
        </div>
      </CardHeader>

      <CardBody className="flex flex-1 flex-col gap-4 px-5 py-4">
        <KpiGroup
          label={companyName}
          dotColor={FOCAL_COLOR}
          labelClass="text-secondary-800"
          values={m.companyValues}
          accent
        />
        <KpiGroup
          label="Peer Average"
          dotColor={PEER_COLORS[0]}
          labelClass="text-neutral-500"
          values={m.peerValues}
          accent={false}
        />

        {/* Chart — pinned to the bottom so every card in the row aligns. */}
        <div className="mt-auto rounded-lg border border-neutral-200 bg-neutral-50/60 px-3 py-3">
          {m.chart}
        </div>
      </CardBody>
    </Card>
  );
}

// ── Chart builders per metric ────────────────────────────────────────────────

function buildRevenueCAGRChart(metrics: FinancialMetricsComparisonResponse, companyName: string): ChartEntry[] {
  const { revenue } = metrics.metrics;
  const data: ChartEntry[] = [
    {
      name: companyName,
      cagr: asNum(revenue?.company?.['10_year_cagr_percentage']),
      isCompany: true,
    },
  ];
  const peers = revenue?.peers as any;
  if (peers?.companies) {
    Object.entries(peers.companies).forEach(([name, m]: [string, any]) => {
      data.push({ name, cagr: asNum(m['10_year_average_percentage']), isCompany: false });
    });
  }
  return data.sort((a, b) => b.cagr - a.cagr);
}

function buildDualBarChart(
  metricBlock: { company: FinancialMetricDetail; peers: FinancialMetricDetail | null } | undefined,
  companyName: string,
  sortAsc = false,
): ChartEntry[] {
  if (!metricBlock) return [];
  const data: ChartEntry[] = [
    {
      name: companyName,
      avg10Year: asNum(metricBlock.company?.['10_year_average_percentage']),
      latest: asNum(metricBlock.company?.latest_percentage),
      isCompany: true,
    },
  ];
  const peers = metricBlock.peers as any;
  if (peers?.companies) {
    Object.entries(peers.companies).forEach(([name, m]: [string, any]) => {
      data.push({
        name,
        avg10Year: asNum(m['10_year_average_percentage']),
        latest: asNum(m.latest_average_percentage),
        isCompany: false,
      });
    });
  }
  return sortAsc
    ? data.sort((a, b) => a.latest - b.latest)
    : data.sort((a, b) => b.latest - a.latest);
}

function asNum(v: any): number {
  if (v == null) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

// ── Main section component ───────────────────────────────────────────────────

interface Props {
  companyName: string;
  competitors?: string[];
}

export default function MetricsComparisonSection({ companyName, competitors }: Props) {
  const { data: metricsData, isLoading, isError } = useFinancialMetricsComparison(companyName, competitors);

  const cards = useMemo<MetricCardEntry[]>(() => {
    if (!metricsData) return [];
    const { metrics } = metricsData;

    const revenueChartData = buildRevenueCAGRChart(metricsData, companyName);
    const ebitdaChartData = buildDualBarChart(metrics.ebitda, companyName);
    const roicChartData = buildDualBarChart(metrics.roic, companyName);
    const waccChartData = buildDualBarChart(metrics.wacc, companyName, true);

    return [
      {
        title: 'Revenue',
        icon: BarChart3,
        companyValues: [
          { label: '10-Year Revenue CAGR', value: formatPct(metrics.revenue?.company?.['10_year_cagr_percentage']) },
        ],
        peerValues: [
          { label: '10-Year Revenue CAGR (Peers)', value: formatPct(metrics.revenue?.peers?.['10_year_cagr_average_percentage']) },
        ],
        performance: getPerformance(
          metrics.revenue?.company?.['10_year_cagr'],
          metrics.revenue?.peers?.['10_year_cagr_average'],
        ),
        chart: (
          <ComparisonBarChart
            data={revenueChartData}
            dataKeys={[{ key: 'cagr', name: '10-Yr CAGR', fill: COLORS.primary }]}
            yLabel="Revenue CAGR (%)"
            tooltipSuffix="%"
          />
        ),
      },
      {
        title: 'EBITDA %',
        icon: Percent,
        companyValues: [
          { label: '10-Year Avg EBITDA %', value: formatVal(metrics.ebitda?.company?.['10_year_average_percentage']) },
          { label: 'Latest EBITDA %', value: formatVal(metrics.ebitda?.company?.latest_percentage) },
        ],
        peerValues: [
          { label: '10-Year Avg (Peers)', value: formatVal(metrics.ebitda?.peers?.['10_year_average_percentage']) },
          { label: 'Latest Avg (Peers)', value: formatVal(metrics.ebitda?.peers?.latest_average_percentage) },
        ],
        performance: getPerformance(
          metrics.ebitda?.company?.['10_year_average'],
          metrics.ebitda?.peers?.['10_year_average'],
        ),
        chart: (
          <ComparisonBarChart
            data={ebitdaChartData}
            dataKeys={[{ key: 'latest', name: 'Latest', fill: COLORS.primary }]}
            yLabel="EBITDA (%)"
          />
        ),
      },
      {
        title: 'ROIC',
        icon: Target,
        companyValues: [
          { label: '10-Year Avg ROIC', value: formatPct(metrics.roic?.company?.['10_year_average_percentage']) },
          { label: 'Latest ROIC', value: formatPct(metrics.roic?.company?.latest_percentage) },
        ],
        peerValues: [
          { label: '10-Year Avg (Peers)', value: formatPct(metrics.roic?.peers?.['10_year_average_percentage']) },
          { label: 'Latest Avg (Peers)', value: formatPct(metrics.roic?.peers?.latest_average_percentage) },
        ],
        performance: getPerformance(
          metrics.roic?.company?.['10_year_average'],
          metrics.roic?.peers?.['10_year_average'],
        ),
        chart: (
          <ComparisonBarChart
            data={roicChartData}
            dataKeys={[{ key: 'latest', name: 'Latest', fill: COLORS.primary }]}
            yLabel="ROIC (%)"
            tooltipSuffix="%"
          />
        ),
      },
      {
        title: 'WACC',
        icon: Scale,
        companyValues: [
          { label: '10-Year Avg WACC', value: formatPct(metrics.wacc?.company?.['10_year_average_percentage']) },
          { label: 'Latest WACC', value: formatPct(metrics.wacc?.company?.latest_percentage) },
        ],
        peerValues: [
          { label: '10-Year Avg (Peers)', value: formatPct(metrics.wacc?.peers?.['10_year_average_percentage']) },
          { label: 'Latest Avg (Peers)', value: formatPct(metrics.wacc?.peers?.latest_average_percentage) },
        ],
        performance: getPerformance(
          metrics.wacc?.company?.['10_year_average'],
          metrics.wacc?.peers?.['10_year_average'],
          false, // lower WACC is better
        ),
        chart: (
          <ComparisonBarChart
            data={waccChartData}
            dataKeys={[{ key: 'latest', name: 'Latest', fill: COLORS.primary }]}
            yLabel="WACC (%)"
            tooltipSuffix="%"
          />
        ),
      },
    ];
  }, [metricsData, companyName]);

  if (isLoading) {
    return (
      <LoadingCard
        title="Peer metrics comparison"
        message="Comparing revenue, EBITDA, ROIC and WACC against peers…"
        height={220}
      />
    );
  }

  if (isError || !metricsData) {
    return (
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Financial metrics comparison unavailable</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Peer comparison data could not be loaded. The company's own financial data is still visible above.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 auto-rows-fr">
      {cards.map((m) => (
        <MetricCard key={m.title} m={m} companyName={companyName} />
      ))}
    </div>
  );
}
